import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { Mail, RefreshCw, Send, Clock, X } from "lucide-react";

/**
 * Admin panel — every sponsored event whose recipient hasn't finished
 * registering. Shows days-pending, last reminder timestamp, and a manual
 * "Resend" button.
 *
 * Backend contract:
 *   GET  /api/admin/sponsor/invites/unclaimed  -> { items, count }
 *   POST /api/admin/sponsor/invites/{event_id}/resend -> { sent, to, days_pending }
 */
const UnclaimedInvitesPanel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/sponsor/invites/unclaimed");
      setItems(r.data.items || []);
    } catch (err) {
      setMsg({ ok: false, text: "Failed to load unclaimed invites" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resend = async (eventId, email) => {
    setBusyId(eventId);
    setMsg(null);
    try {
      const r = await api.post(`/admin/sponsor/invites/${eventId}/resend`);
      setMsg({
        ok: !!r.data.sent,
        text: r.data.sent
          ? `Invite resent to ${email}`
          : `Attempted to resend to ${email} — check SMTP config`,
      });
      await load();
    } catch (err) {
      setMsg({ ok: false, text: err?.response?.data?.detail || "Failed to resend" });
    } finally {
      setBusyId(null);
    }
  };

  const cancel = async (eventId, email) => {
    if (!window.confirm(`Cancel and delete this unclaimed sponsored event for ${email}?\n\nThe recipient has NOT completed registration, so no user data will be lost. This action cannot be undone.`)) return;
    setBusyId(eventId);
    setMsg(null);
    try {
      await api.delete(`/admin/sponsor/invites/${eventId}`);
      setMsg({ ok: true, text: `Cancelled invite for ${email}` });
      await load();
    } catch (err) {
      setMsg({ ok: false, text: err?.response?.data?.detail || "Failed to cancel invite" });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div data-testid="admin-unclaimed-invites-card" className="bg-white rounded-2xl soft-shadow p-6 space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Mail className="w-5 h-5 text-coral-400" />
          <h2 className="font-semibold text-navy-700 text-lg">Unclaimed sponsor invites</h2>
        </div>
        <button
          onClick={load}
          data-testid="unclaimed-invites-refresh"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-navy-700/60 hover:text-navy-700"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>
      <p className="text-sm text-navy-700/60 -mt-2">
        Sponsored events whose recipients haven&apos;t finished registration yet. Day-3 and day-7 reminder emails are sent automatically; use <em>Resend</em> for an out-of-band nudge.
      </p>

      {msg && (
        <div
          data-testid="unclaimed-invites-msg"
          className={`text-sm rounded-md px-3 py-2 ${msg.ok ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`}
        >
          {msg.text}
        </div>
      )}

      {loading ? (
        <p data-testid="unclaimed-invites-loading" className="text-sm text-navy-700/50">Loading…</p>
      ) : items.length === 0 ? (
        <p data-testid="unclaimed-invites-empty" className="text-sm text-navy-700/50">All sponsored events have been claimed.</p>
      ) : (
        <div data-testid="unclaimed-invites-list" className="divide-y divide-cream-200">
          {items.map((it) => (
            <div
              key={it.event_id}
              data-testid={`unclaimed-invite-row-${it.event_id}`}
              className="py-3 flex flex-wrap items-center justify-between gap-3 text-sm"
            >
              <div className="min-w-0 flex-1">
                <div className="font-medium text-navy-700 truncate">{it.event_name}</div>
                <div className="text-navy-700/50 text-xs truncate">
                  {it.host_email} · {it.tier}
                  {it.last_reminder_at && (
                    <>
                      {" · "}last reminder {new Date(it.last_reminder_at).toLocaleDateString()}
                      {it.last_reminder_kind && ` (${it.last_reminder_kind})`}
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span
                  data-testid={`unclaimed-invite-days-${it.event_id}`}
                  className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider rounded px-2 py-1 font-bold ${
                    it.days_pending >= 7
                      ? "bg-red-50 text-red-600"
                      : it.days_pending >= 3
                      ? "bg-amber-50 text-amber-700"
                      : "bg-cream-100 text-navy-700/60"
                  }`}
                >
                  <Clock className="w-3 h-3" />
                  {it.days_pending}d pending
                </span>
                <button
                  onClick={() => resend(it.event_id, it.host_email)}
                  disabled={busyId === it.event_id}
                  data-testid={`unclaimed-invite-resend-${it.event_id}`}
                  className="inline-flex items-center gap-1.5 bg-coral-300 text-white px-3 py-1.5 rounded-full text-xs font-semibold hover:bg-coral-400 disabled:opacity-60"
                >
                  <Send className="w-3.5 h-3.5" />
                  {busyId === it.event_id ? "..." : "Resend"}
                </button>
                <button
                  onClick={() => cancel(it.event_id, it.host_email)}
                  disabled={busyId === it.event_id}
                  data-testid={`unclaimed-invite-cancel-${it.event_id}`}
                  title="Cancel this unclaimed invite"
                  className="inline-flex items-center gap-1.5 border border-red-300 text-red-600 px-3 py-1.5 rounded-full text-xs font-semibold hover:bg-red-50 disabled:opacity-60"
                >
                  <X className="w-3.5 h-3.5" />
                  Cancel
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UnclaimedInvitesPanel;
